import { IDynamicModel } from '../models';

// WI 34928: show/hide rules (showOnValues, a checkbox's hiddenFormControlName) used to compare with
// values.includes(value) and push each source's own hidden flag straight to the target. A
// multi-select holds an array, so it never matched, and two sources revealing one target fought:
// the last one to change won. These helpers keep a single-value rule exactly as it was.

const RECORD_KEYS = ['value', 'Id', 'id'];

function optionValue(option: unknown, valueProperty?: string): unknown {
  if (option === null || typeof option !== 'object') {
    return option;
  }

  const record = option as Record<string, unknown>;
  const key = [valueProperty, ...RECORD_KEYS].find(
    (k) => !!k && record[k] !== undefined
  );
  return key ? record[key] : undefined;
}

/**
 * True when a control's value reveals the targets of its showOnValues rule. A single value must be
 * one of `values`. A multi-select (an array of values or records) matches when any picked option is
 * one of `values`; a record is compared by `valueProperty`, then `value`, `Id` or `id`.
 */
export function dynamicShowOnValuesMatch(
  values: ReadonlyArray<unknown> | null | undefined,
  value: unknown,
  valueProperty?: string,
  isMultiSelect = false
): boolean {
  if (!values?.length) {
    return false;
  }
  // A saved multi-select answer is a comma list of ids (dynamicSerializeControlValue).
  const options = Array.isArray(value)
    ? value
    : isMultiSelect && typeof value === 'string'
      ? value.split(',').map((id) => id.trim())
      : [value];
  return options.some((option) =>
    values.includes(optionValue(option, valueProperty))
  );
}

/**
 * Tracks, per target, what each source control last reported. A target is hidden only while every
 * source that has reported says hidden, so two controls can reveal the same field (an OR). With one
 * source the result is that source's own flag, which is the old behaviour.
 */
export class DynamicTargetVisibility {
  private readonly hiddenBySource = new Map<string, Map<string, boolean>>();

  report(
    source: string | undefined,
    target: string | undefined,
    hidden: boolean
  ): boolean {
    if (!source || !target) {
      return hidden;
    }
    let sources = this.hiddenBySource.get(target);
    if (!sources) {
      sources = new Map<string, boolean>();
      this.hiddenBySource.set(target, sources);
    }

    sources.set(source, hidden);
    return [...sources.values()].every((isHidden) => isHidden);
  }
}

// One tracker per rendered form, keyed by its root FormGroup, so state never leaks between forms
// and is collected with the form.
const visibilityByForm = new WeakMap<object, DynamicTargetVisibility>();

/**
 * Records that `source` wants `target` hidden or shown on `form`, and returns whether the target
 * should be hidden now. Without a form the source's own flag is returned unchanged.
 */
export function dynamicReportTargetVisibility(
  form: object | null | undefined,
  source: string | undefined,
  target: string | undefined,
  hidden: boolean
): boolean {
  if (!form) {
    return hidden;
  }

  let visibility = visibilityByForm.get(form);
  if (!visibility) {
    visibility = new DynamicTargetVisibility();
    visibilityByForm.set(form, visibility);
  }
  return visibility.report(source, target, hidden);
}

/** Forgets every source's report on `form`, so a reset form starts with no stale "shown" (#34303). */
export function dynamicClearTargetVisibility(form: object): void {
  visibilityByForm.delete(form);
}

/**
 * Emits a showOnValues source's hidden flag to each of its targets, OR-ed with every other source
 * that reveals the same target on this form.
 */
export function dynamicEmitShowOnValuesTargets(
  update$: { next(update: IDynamicModel): void },
  form: object | null | undefined,
  source: string | undefined,
  showOnValues:
    { dynamicControlNames?: string[]; formControlName?: string } | undefined,
  isHidden: boolean
): void {
  const targets = new Set([
    ...(showOnValues?.dynamicControlNames || []),
    showOnValues?.formControlName,
  ]);
  targets.forEach((target) =>
    update$.next({
      formControlName: target,
      type: 'target',
      options: {
        hidden: dynamicReportTargetVisibility(form, source, target, isHidden),
      },
    } as IDynamicModel)
  );
}
